import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '@figma/fpl-components';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { Trans } from '@/UI/Trans/Trans';
import {
  AppDialogSection,
  AppDialog,
  AppDialogActions,
  AppDialogContent,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { Box } from '@/UI/Box/Box';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { Link } from '@/UI/Link/Link';
import { Skeleton } from '@/UI/Skeleton/Skeleton';
import { TextStdRg, TextSmRg, Typography } from '@/UI/AppTypography/AppTypography';
import { log } from '@/logger/logger.ts';
import { color } from '@/colors';
import { formatPrice } from '@/consts/pricing.consts';
import { BillingCycle } from '@/types/shared';
import { User } from '@/types/auth.types';
import { I18N_KEYS } from '@/language/keys';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { PricingPackage } from '@/components/SubscriptionsAndPayments/payments.types';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';

const logger = log.getLogger('ConfirmUpgradeModal');

interface ConfirmUpgradeModalProps {
  open: boolean;
  onClose: () => void;
  toPackage: PricingPackage;
  onConfirm: (toPackage: PricingPackage, numberOfSeats?: number) => Promise<void>;
  isProcessing: boolean;
  hasError: boolean;
  currentUser: User;
}

function UpgradeErrorBanner() {
  const { t } = useTranslation();
  const isFpl = useFplMigrationFlag();

  const openIntercom = () => {
    if (window.Intercom) {
      window.Intercom('show');
    }
  };

  if (isFpl) {
    return (
      <Box sx={{ width: '100%', mb: 3, mt: 1 }}>
        <Banner.InsetModal variant="danger">
          <Banner.Message>
            <Trans
              i18nKey={I18N_KEYS.PAYMENTS.UPGRADE_MODAL.ERROR_MESSAGE}
              components={{
                link: (
                  <Banner.ButtonLink onClick={openIntercom}>
                    {t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.ERROR_MESSAGE_LINK)}
                  </Banner.ButtonLink>
                ),
              }}
            />
          </Banner.Message>
        </Banner.InsetModal>
      </Box>
    );
  }

  return (
    <Flex
      sx={{
        width: '100%',
        mb: 3,
        backgroundColor: color.Weavy_Error_08_T,
        border: `1px solid ${color.Weavy_Error_64_T}`,
        borderRadius: 1,
        p: 2,
        mt: 1,
      }}
    >
      <TextSmRg color={color.Weavy_Error}>
        <Trans
          i18nKey={I18N_KEYS.PAYMENTS.UPGRADE_MODAL.ERROR_MESSAGE}
          components={{
            link: (
              <ButtonTextPlain onClick={openIntercom} color={color.Weavy_Error} variant="body-sm-rg">
                {t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.ERROR_MESSAGE_LINK)}
              </ButtonTextPlain>
            ),
          }}
        />
      </TextSmRg>
    </Flex>
  );
}

function ConfirmUpgradeModal({
  open,
  onClose,
  toPackage,
  onConfirm,
  isProcessing,
  hasError,
  currentUser,
}: ConfirmUpgradeModalProps) {
  const { t } = useTranslation();

  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [paymentMethodBrand, setPaymentMethodBrand] = useState<string | null>(null);
  const [paymentMethodLast4, setPaymentMethodLast4] = useState<string | null>(null);
  const [customerPortalSessionLink, setCustomerPortalSessionLink] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLocalError, setHasLocalError] = useState(false);

  useEffect(() => {
    // get user payment info.
    const getPaymentMethod = async () => {
      try {
        setHasLocalError(false);
        setIsLoading(true);
        const [paymentMethodRes, customerPortalSessionRes] = await Promise.all([
          safeAxios.get(
            API_ROUTES.workspaces.subscriptionPaymentMethod({ workspaceId: currentUser?.activeWorkspace?.workspaceId }),
          ),
          safeAxios.post(
            API_ROUTES.workspaces.subscriptionCustomerPortalSession({
              workspaceId: currentUser?.activeWorkspace?.workspaceId,
            }),
            {
              returnUrl: window.location.href,
            },
          ),
        ]);
        setPaymentMethodBrand(paymentMethodRes.data?.card?.brand);
        setPaymentMethodLast4(paymentMethodRes.data?.card?.last4Numbers);
        setPaymentMethod(paymentMethodRes.data?.type);
        setCustomerPortalSessionLink(customerPortalSessionRes.data.link);
      } catch (error) {
        logger.error('Failed getting payment method', error);
        setHasLocalError(true);
      } finally {
        setIsLoading(false);
      }
    };
    void getPaymentMethod();
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, []);

  const isYearly = toPackage.billingCycle !== BillingCycle.Monthly;
  const totalPrice = (isYearly ? toPackage.annualTotal : toPackage.price) * (toPackage.numberOfSeats || 1);
  const priceLabel = t(
    isYearly ? I18N_KEYS.PAYMENTS.UPGRADE_MODAL.PRICE_PER_YEAR : I18N_KEYS.PAYMENTS.UPGRADE_MODAL.PRICE_PER_MONTH,
    { price: formatPrice(totalPrice) },
  );
  const paymentMethodLabel = t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.PAYMENT_METHOD_LABEL);

  return (
    <AppDialog open={open} onClose={onClose} width="md" slotProps={{ paper: { sx: { backgroundColor: token.bg } } }}>
      <AppDialogSection>
        <AppDialogHeader component="div" sx={{ p: 3, pt: 5, pb: 0 }}>
          <AppDialogTitle>
            {isLoading ? (
              <Skeleton variant="text" width={160} height={24} />
            ) : (
              t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.TITLE)
            )}
          </AppDialogTitle>
          <AppDialogXBtn sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} />
        </AppDialogHeader>
        <AppDialogContent sx={{ px: 3, pb: 0, '&&': { pt: 3 } }}>
          {(hasError || hasLocalError) && <UpgradeErrorBanner />}
          <Flex sx={{ justifyContent: 'space-between', alignItems: 'flex-end', mt: 1 }}>
            <FlexCol sx={{ gap: 0.25 }}>
              <Typography variant="body-std-rg">
                {isLoading ? (
                  <Skeleton variant="text" width={160} height={20} />
                ) : (
                  <Trans
                    i18nKey={I18N_KEYS.PAYMENTS.UPGRADE_MODAL.PLAN_DESCRIPTION}
                    components={{ plan: <span style={{ textTransform: 'capitalize' }}>{toPackage.name}</span> }}
                  />
                )}
              </Typography>
              <TextStdRg color={token.textSecondary}>
                {isLoading ? (
                  <Skeleton variant="text" width={100} height={16} />
                ) : (
                  <Trans
                    i18nKey={I18N_KEYS.PAYMENTS.UPGRADE_MODAL.BILLING_CYCLE_INFO}
                    components={{ billingCycle: <span style={{}}>{toPackage.billingCycle}</span> }}
                  />
                )}
              </TextStdRg>
            </FlexCol>
            <FlexCol>
              <Typography variant="body-std-rg">
                {isLoading ? <Skeleton variant="text" width={80} height={20} /> : priceLabel}
              </Typography>
            </FlexCol>
          </Flex>
          <Box sx={{ my: 2, width: '100%' }}>
            <AppDivider />
          </Box>
          <FlexCenVer sx={{ justifyContent: 'space-between' }}>
            <Typography variant="body-std-rg">
              {isLoading ? <Skeleton variant="text" width={100} height={16} /> : paymentMethodLabel}
            </Typography>
            <FlexCol sx={{ alignItems: 'baseline' }}>
              <Typography variant="body-std-rg">
                {isLoading ? (
                  <Skeleton variant="text" width={100} height={16} />
                ) : paymentMethod === 'card' ? (
                  `${paymentMethodBrand} *${paymentMethodLast4}`
                ) : (
                  paymentMethod
                )}
              </Typography>
            </FlexCol>
          </FlexCenVer>
          <FlexCenVer sx={{ justifyContent: 'flex-end', pb: 1 }}>
            {isLoading ? (
              <Skeleton variant="text" width={50} height={12} />
            ) : (
              <>
                <Link href={customerPortalSessionLink ?? ''} target="_blank">
                  <Typography variant="body-std-rg" color={token.textSecondary}>
                    {t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.CHANGE_PAYMENT_METHOD)}
                  </Typography>
                </Link>
              </>
            )}
          </FlexCenVer>
        </AppDialogContent>
        <AppDialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3, pt: 3 }}>
          <ButtonContained size="small" mode="outlined" onClick={onClose} disabled={isLoading}>
            {t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.CTA_CANCEL)}
          </ButtonContained>
          <ButtonContained
            startIcon={isProcessing ? <CircularProgress size={16} sx={{ color: color.Black92 }} /> : null}
            onClick={() => void onConfirm(toPackage, toPackage.numberOfSeats)}
            disabled={isLoading || isProcessing}
            size="small"
          >
            {isProcessing
              ? t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.PROCESSING)
              : t(I18N_KEYS.PAYMENTS.UPGRADE_MODAL.CTA_CONFIRM)}
          </ButtonContained>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
}

export default ConfirmUpgradeModal;
